import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import React, { ReactNode } from "react";

import { useOnElementClick } from "@common/hooks/useOnElementClick";
import { AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";

import * as Styled from "./Modal.styles";
import { ModalFooter } from "./ModalFooter/ModalFooter";
import { ModalHeader, ModalHeaderProps } from "./ModalHeader/ModalHeader";
import { useEscapeKey } from "../../hooks/useOnEscapeModal";

export interface ModalProps {
  active?: boolean;
  setActive?: (active: boolean) => void;
  size?: "wide" | "narrow" | "narrower" | "full" | "fuller";
  buttonElement?: JSX.Element;
  leftButtonElement?: JSX.Element;
  additionalElement?: JSX.Element;
  buttonText?: string;
  children?: ReactNode;
  noAnimation?: boolean;
  heading?: string;
  iconPath?: string;
  disableScroll?: boolean;
  hasSeparator?: boolean;
  onOutsideClick?: () => void;
  hash?: string;
  allowOpen?: boolean;
  autoOpen?: boolean;
}

const Modal = React.forwardRef((props: ModalProps & ModalHeaderProps, ref) => {
  const {
    children,
    size,
    active,
    setActive,
    noAnimation,
    buttonElement,
    leftButtonElement,
    additionalElement,
    buttonText,
    disableScroll,
    hasSeparator = true,
    ...headerProps
  } = props;

  return (
    <AnimatePresence>
      {active && (
        <Styled.ModalOverlay
          $active={active}
          variants={!noAnimation ? Styled.ModalVariant : undefined}
          initial={"initial"}
          animate={"isOpen"}
          exit={"exit"}
        >
          <Styled.Modal
            ref={ref as RefObject<HTMLDivElement>}
            className="modal"
            $active={active}
            $size={size}
            variants={!noAnimation ? Styled.ContainerVariant : undefined}
          >
            <Styled.ModalInner className="modal-inner">
              <Styled.ModalCard>
                <ModalHeader
                  {...headerProps}
                  hasSeparator={hasSeparator}
                  noContent={!Boolean(children)}
                />
                <Styled.ModalCardScrollWrap>
                  <Styled.ModalCardScroll disableScroll={disableScroll}>{children}</Styled.ModalCardScroll>
                </Styled.ModalCardScrollWrap>
                <ModalFooter
                  additionalElement={additionalElement}
                  leftButton={leftButtonElement}
                  button={buttonElement}
                  buttonText={buttonText}
                  toggle={() => setActive?.(false)}
                  hasSeparator={hasSeparator}
                />
              </Styled.ModalCard>
            </Styled.ModalInner>
          </Styled.Modal>
        </Styled.ModalOverlay>
      )}
    </AnimatePresence>
  );
});

export function useModal({
  size,
  active: activeProp = false,
  onOutsideClick,
  hash,
  allowOpen = true,
  autoOpen = true,
}: Pick<ModalProps, "size" | "active" | "onOutsideClick" | "hash" | "allowOpen" | "autoOpen">) {
  const location = useLocation();

  const [active, setActive] = useState(activeProp);
  const modalRef = useRef<HTMLDivElement | null>(null);

  const clickOutsideHandlerRef = useCallback(() => {
    onOutsideClick ? onOutsideClick() : setActive(false);
  }, [onOutsideClick]);

  const toggleRef = useRef(() => {
    if (!allowOpen && !active) return;
    setActive((currentActive) => !currentActive);
  });

  useOnElementClick(modalRef, clickOutsideHandlerRef);
  useEscapeKey(setActive);

  useEffect(() => {
    if (!hash || !allowOpen || !autoOpen) return;

    if (location.hash === hash) setActive(true);
  }, [hash, allowOpen, location.hash, autoOpen]);

  useEffect(() => {
    if (!hash || !allowOpen) return;

    const url = new URL(window.location.href);
    if (active && window.location.hash !== hash) {
      url.hash = hash;
      window.history.pushState(null, "", url.toString());
    }
    if (!active && window.location.hash === hash) {
      url.hash = "";
      window.history.replaceState(null, "", url.toString());
    }
  }, [active, allowOpen, hash]);

  useEffect(() => {
    if (!hash || !allowOpen) return;

    const handlePopState = () => {
      setActive(window.location.hash === hash);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [hash, allowOpen]);

  return {
    modalProps: {
      active,
      size,
      ref: modalRef,
      setActive,
    },
    Modal,
    toggle: toggleRef.current,
    active,
    setActive,
  };
}

Modal.displayName = "Modal";
export default Modal;
